import { Component, inject, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UploadFileComponent } from '@uigy/shared/components/upload-file/upload-file.component';
import { LanguageService } from '@uigy/shared/services/language.service';
import { Subject } from 'rxjs';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import {
  MAX_FILE_SIZE_VALIDATOR_KEY,
  MaxFileSizeValidatorDirective,
} from '@uigy/shared/directives/max-file-size-validator.directive';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { finalize, take } from 'rxjs/operators';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { UIButtonComponent } from '@uigy/shared/components/ui-button/ui-button.component';
import { CardBoxComponent } from '@uigy/shared/components';
import { AppIdDirective } from '@uigy/shared/directives/app-id/app-id.directive';
import { NotificationComponent } from '@uigy/shared/components/info/notification.component';
import { OtdScansService } from '@uigy/modules/active-queries/components/otd-scans/services/otd-scans.service';

@Component({
  selector: 'app-otd-scan-file-upload',
  templateUrl: './otd-scan-file-upload.component.html',
  styleUrls: ['./otd-scan-file-upload.component.scss'],
  imports: [
    UploadFileComponent,
    UIButtonComponent,
    CardBoxComponent,
    MaxFileSizeValidatorDirective,
    ReactiveFormsModule,
    AppIdDirective,
    NotificationComponent,
  ],
  standalone: true,
})
export class OtdScanFileUploadComponent {
  @ViewChild('otdScanFileUpload', {read: UploadFileComponent}) fileUpload?: UploadFileComponent;

  private readonly languageService = inject(LanguageService);
  private readonly otdScansService = inject(OtdScansService);
  private readonly toasterService = inject(ToasterService);

  public readonly texts = this.languageService.texts;
  public readonly otdScansTexts = this.languageService.texts.queries.active_queries.otd_scans.upload_card;

  public readonly AppIdEnum = AppIdsEnum;
  protected readonly IconsEnum = IconsEnum;
  public readonly toasterType = ToasterType;

  public fileControl = new UntypedFormControl(null);

  public isLoading = signal(false);
  public isDisabled = false;

  public _refetchData$ = new Subject<void>();

  protected readonly AppIdsEnum = AppIdsEnum;

  public get refetchData$(): Subject<void> {
    return this._refetchData$;
  }

  constructor() {}

  public uploadFile(): void {
    this.fileUpload?.fileInputHtmlElement.nativeElement.click();
  }

  public onFileChanged(fileComponent: UploadFileComponent): void {
    const file: File | undefined = this.fileControl.value.get('file');
    if (!file) {
      return;
    }

    if (!this.fileControl.valid) {
      let invalidMsg = this.texts.general.invalid_file.general_error;
      if (this.fileControl.errors && Object.keys(this.fileControl.errors).includes(MAX_FILE_SIZE_VALIDATOR_KEY)) {
        invalidMsg = this.texts.general.invalid_file.max_size;
      }
      this.toasterService.openToaster({ message: invalidMsg, type: ToasterType.Error });
      return;
    }

    this.uploadOtdScanFile(file, fileComponent);
  }

  private uploadOtdScanFile(file: File, fileComponent: UploadFileComponent): void {
    this.isLoading.set(true);
    const formData = new FormData();
    formData.append('file', file);

    this.otdScansService.uploadOtdScanFile(file).pipe(
      take(1),
      finalize(() => {
        this.isLoading.set(false);
        fileComponent.fileInputHtmlElement.nativeElement.value = '';
        this.fileControl.setValue(null);
      })
    ).subscribe(() => {
      this.toasterService.openToaster({
        type: ToasterType.Success,
        message: this.otdScansTexts.upload_success,
      });
      this.refetchData$.next();
    });
  }
}
